import { useEffect, useRef, useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Camera, CameraOff, Loader2, AlertCircle, RefreshCw } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface QRScannerProps {
  onScanSuccess: (orderId: string, otpCode: string) => void;
  onClose: () => void;
}

const QRScanner = ({ onScanSuccess, onClose }: QRScannerProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const animationRef = useRef<number | null>(null);
  const lastInvalidScanRef = useRef<number>(0);
  
  const [cameraActive, setCameraActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isInitializing, setIsInitializing] = useState(true);
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([]);
  const [selectedDeviceId, setSelectedDeviceId] = useState<string>('');
  const { toast } = useToast();

  // Dynamically import jsQR for QR code detection
  const scanQRCode = useCallback(async () => {
    if (!videoRef.current || !canvasRef.current || !cameraActive) return;
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    if (!ctx || video.readyState !== video.HAVE_ENOUGH_DATA) {
      animationRef.current = requestAnimationFrame(scanQRCode);
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    
    // Use jsQR library
    try {
      const jsQR = (await import('jsqr')).default;
      const code = jsQR(imageData.data, imageData.width, imageData.height, {
        inversionAttempts: 'dontInvert',
      });
      
      if (code) {
        const text = code.data;
        console.log('QR Code scanned:', text);
        
        // Parse QR code data: FORMAT = "ORDER:{orderId}:{otpCode}" or "VENDOR:{orderId}:{otpCode}"
        if (text.startsWith('ORDER:') || text.startsWith('VENDOR:')) {
          const parts = text.split(':');
          if (parts.length === 3) {
            const orderId = parts[1];
            const otpCode = parts[2];
            
            stopCamera();
            onScanSuccess(orderId, otpCode);
            
            toast({
              title: "QR Code scanné !",
              description: "Validation en cours..."
            });
            return;
          }
        }
        
        // Only show "invalid QR" toast once every 5 seconds to avoid spam
        const now = Date.now();
        if (now - lastInvalidScanRef.current > 5000) {
          lastInvalidScanRef.current = now;
          toast({
            title: "QR Code non reconnu",
            description: "Scannez un QR code de commande Nazandoo",
            variant: "destructive"
          });
        }
      }
    } catch (err) {
      console.error('Error scanning:', err);
    }
    
    animationRef.current = requestAnimationFrame(scanQRCode);
  }, [cameraActive, onScanSuccess, toast]);

  const startCamera = async (deviceId?: string) => {
    setIsInitializing(true);
    setError(null);
    setCameraActive(false);
    
    try {
      // Stop any existing stream first and wait
      stopCamera();
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const constraints: MediaStreamConstraints = {
        video: deviceId 
          ? { 
              deviceId: { exact: deviceId },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
          : { 
              facingMode: { ideal: 'environment' },
              width: { ideal: 1280 },
              height: { ideal: 720 }
            }
      };
      
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        
        // Wait for video to be ready
        await new Promise<void>((resolve, reject) => {
          const video = videoRef.current!;
          const timeout = setTimeout(() => reject(new Error('Timeout starting video source')), 10000);
          
          video.onloadedmetadata = () => {
            clearTimeout(timeout);
            video.play()
              .then(() => resolve())
              .catch(reject);
          };
          
          video.onerror = () => {
            clearTimeout(timeout);
            reject(new Error('Video error'));
          };
        });
        
        setCameraActive(true);
        setIsInitializing(false);
        
        // Start scanning
        animationRef.current = requestAnimationFrame(scanQRCode);
      }
      
      // Get list of cameras if not already done
      if (devices.length === 0) {
        const allDevices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = allDevices.filter(d => d.kind === 'videoinput');
        setDevices(videoDevices);
        if (videoDevices.length > 0 && !selectedDeviceId) {
          setSelectedDeviceId(videoDevices[0].deviceId);
        }
      }
      
    } catch (err: any) {
      console.error('Camera error:', err);
      setIsInitializing(false);
      setCameraActive(false);
      
      if (err.name === 'NotAllowedError') {
        setError('Permission caméra refusée. Autorisez l\'accès dans les paramètres du navigateur.');
      } else if (err.name === 'NotFoundError') {
        setError('Aucune caméra détectée.');
      } else if (err.name === 'NotReadableError' || err.name === 'AbortError') {
        setError('La caméra est occupée. Fermez les autres apps qui l\'utilisent et réessayez.');
      } else if (err.message?.includes('Timeout')) {
        setError('La caméra met trop de temps à démarrer. Réessayez.');
      } else {
        setError(`Erreur: ${err.message}`);
      }
    }
  };

  const stopCamera = () => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
    
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => {
        track.stop();
      });
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
      videoRef.current.load(); // Reset video element
    }
    
    setCameraActive(false);
  };

  const handleDeviceChange = async (deviceId: string) => {
    setSelectedDeviceId(deviceId);
    await startCamera(deviceId);
  };

  useEffect(() => {
    startCamera();
    return () => stopCamera();
  }, []);

  useEffect(() => {
    if (cameraActive) {
      animationRef.current = requestAnimationFrame(scanQRCode);
    }
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [cameraActive, scanQRCode]);

  return (
    <Card className="p-4 sm:p-6">
      <div className="space-y-4">
        {/* Error Message */}
        {error && (
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium text-destructive">Erreur caméra</p>
                <p className="text-sm text-destructive/80 mt-1">{error}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 w-full"
              onClick={() => startCamera(selectedDeviceId)}
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Réessayer
            </Button>
          </div>
        )}

        {/* Loading State */}
        {isInitializing && !error && (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Activation de la caméra...</p>
            </div>
          </div>
        )}

        {/* Camera Selection */}
        {!error && devices.length > 1 && (
          <div>
            <label className="block text-sm font-medium mb-2">
              Caméra
            </label>
            <select
              className="w-full p-2 border rounded-md text-sm"
              value={selectedDeviceId}
              onChange={(e) => handleDeviceChange(e.target.value)}
            >
              {devices.map((device, idx) => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Caméra ${idx + 1}`}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Video Preview */}
        {!error && (
          <div className="relative aspect-square bg-black rounded-lg overflow-hidden max-h-[50vh]">
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              playsInline
              autoPlay
              muted
            />
            <canvas ref={canvasRef} className="hidden" />
            
            {/* Scanning overlay */}
            {cameraActive && (
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 sm:w-56 sm:h-56">
                  <div className="border-2 border-primary rounded-lg w-full h-full relative">
                    <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-primary rounded-tl-lg" />
                    <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-primary rounded-tr-lg" />
                    <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-primary rounded-bl-lg" />
                    <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-primary rounded-br-lg" />
                  </div>
                  {/* Scanning line animation */}
                  <div className="absolute inset-x-2 h-0.5 bg-primary animate-scan-line" />
                </div>
                {/* Status indicator */}
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-3 py-1.5 rounded-full text-xs flex items-center gap-2">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                  Scan en cours...
                </div>
              </div>
            )}
          </div>
        )}

        {/* Controls */}
        <div className="flex flex-col sm:flex-row gap-2">
          {cameraActive && (
            <Button 
              onClick={stopCamera} 
              variant="outline" 
              className="flex-1"
            >
              <CameraOff className="h-4 w-4 mr-2" />
              Arrêter
            </Button>
          )}
          
          {!cameraActive && !isInitializing && !error && (
            <Button 
              onClick={() => startCamera(selectedDeviceId)} 
              className="flex-1"
            >
              <Camera className="h-4 w-4 mr-2" />
              Démarrer
            </Button>
          )}
          
          <Button variant="outline" onClick={onClose} className="flex-1">
            Annuler
          </Button>
        </div>

        {/* Instructions */}
        {!error && (
          <div className="text-xs sm:text-sm text-muted-foreground">
            <p>Placez le QR code dans le cadre pour le scanner automatiquement.</p>
          </div>
        )}
      </div>
    </Card>
  );
};

export default QRScanner;
